import { useEffect, useState } from "react";
import { api, notifyError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, UserCog, Trash2, Loader2, Mail, Pencil } from "lucide-react";

const empty = {
  email: "",
  password: "",
  name: "",
  role: "personel",
  phone: "",
  address: "",
  city: "",
  district: "",
  invoice_type: "individual",
  invoice_title: "",
  tax_no: "",
  tax_office: "",
  invoice_address: "",
  billing_email: "",
};

const fromStaff = (s) => ({
  ...empty,
  email: s.email || "",
  password: "",
  name: s.name || "",
  role: s.role || "personel",
  phone: s.phone || "",
  address: s.address || "",
  city: s.city || "",
  district: s.district || "",
  invoice_type: s.invoice_type || "individual",
  invoice_title: s.invoice_title || "",
  tax_no: s.tax_no || "",
  tax_office: s.tax_office || "",
  invoice_address: s.invoice_address || "",
  billing_email: s.billing_email || "",
});

export default function Staff() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/staff");
      setList(data);
      return data;
    } catch (e) {
      notifyError(e);
      return null;
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // Master-panel edits to owner profile/billing land in the same users row
    // we display here. Refresh whenever the tab regains focus so changes made
    // in another window/session show up without a manual reload.
    const onFocus = () => { load(); };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(empty);
    setOpen(true);
  };

  const openEdit = async (s) => {
    // Pull the freshest copy before opening — guards against stale list state
    // (e.g. master updated this owner from another tab seconds ago).
    let target = s;
    try {
      const data = await load();
      if (data) {
        const fresh = data.find((x) => x.id === s.id);
        if (fresh) target = fresh;
      }
    } catch {
      /* fall back to the row we already have */
    }
    setEditingId(target.id);
    setForm(fromStaff(target));
    setOpen(true);
  };

  const buildPayload = () => {
    const payload = { ...form };
    // Strip empty password on edit so we don't overwrite it.
    if (editingId && !payload.password) delete payload.password;
    // Normalize empty strings to null so the backend doesn't store "".
    Object.keys(payload).forEach((k) => {
      if (typeof payload[k] === "string" && payload[k].trim() === "") {
        payload[k] = null;
      }
    });
    return payload;
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = buildPayload();
      if (editingId) {
        await api.put(`/staff/${editingId}`, payload);
        toast.success("Personel güncellendi");
      } else {
        await api.post("/staff", payload);
        toast.success("Personel eklendi");
      }
      setOpen(false);
      setEditingId(null);
      setForm(empty);
      await load();
    } catch (err) {
      notifyError(err);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/staff/${deleteId}`);
      toast.success("Personel silindi");
      setDeleteId(null);
      await load();
    } catch (err) {
      notifyError(err);
    }
  };

  const isEdit = !!editingId;

  return (
    <div className="p-4 sm:p-8 max-w-5xl mx-auto space-y-6" data-testid="staff-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow text-amber-600">Ayarlar</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-2">Personel</h1>
          <p className="text-sm text-muted-foreground mt-2">
            İşletmenize personel ekleyin. Personeller müşteri/emanet ekleyebilir, teslim alabilir, ödeme alır. Silme ve personel yönetimi sadece yöneticiye açıktır.
            Adres ve fatura bilgileri lisans ödemeleri için kullanılır. Paket satınalabilmek için yöneticinin telefon ve adres bilgileri eksiksiz olmalıdır.
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
          data-testid="add-staff-btn"
        >
          <Plus className="h-4 w-4 mr-2" /> Yeni Personel
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-16"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
      ) : (
        <Card className="rounded-sm border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-foreground text-background">
                <tr>
                  <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Ad</th>
                  <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">E-posta</th>
                  <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Telefon</th>
                  <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Rol</th>
                  <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Eklendi</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {list.map((s) => (
                  <tr key={s.id} data-testid={`staff-row-${s.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-sm bg-muted flex items-center justify-center">
                          <UserCog className="h-4 w-4" />
                        </div>
                        <div className="font-semibold">{s.name}</div>
                        {s.id === user?.id && <Badge variant="outline" className="rounded-sm text-xs">Siz</Badge>}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">
                      <div className="flex items-center gap-1.5"><Mail className="h-3 w-3 text-muted-foreground" />{s.email}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                      {s.phone || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={`rounded-sm uppercase tracking-wider text-xs ${s.role === "owner" ? "bg-amber-500 text-white hover:bg-amber-500" : "bg-secondary text-secondary-foreground hover:bg-secondary"}`}>
                        {s.role === "owner" ? "Yönetici" : "Personel"}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-muted-foreground">
                      {new Date(s.created_at).toLocaleDateString("tr-TR")}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEdit(s)}
                          className="p-2 hover:bg-muted rounded-sm"
                          data-testid={`edit-staff-${s.id}`}
                          aria-label="Düzenle"
                          title="Düzenle"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        {s.id !== user?.id && (
                          <button
                            onClick={() => setDeleteId(s.id)}
                            className="p-2 hover:bg-destructive/10 hover:text-destructive rounded-sm"
                            data-testid={`delete-staff-${s.id}`}
                            aria-label="Sil"
                            title="Sil"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              {isEdit ? "Personeli Düzenle" : "Yeni Personel"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-5" data-testid="staff-form">
            <div>
              <div className="label-eyebrow text-amber-600 mb-2">Temel Bilgiler</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="label-eyebrow">Ad Soyad *</Label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="staff-name" />
                </div>
                <div className="space-y-1.5">
                  <Label className="label-eyebrow">E-posta *</Label>
                  <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm" data-testid="staff-email" />
                </div>
                <div className="space-y-1.5">
                  <Label className="label-eyebrow">
                    {isEdit ? "Yeni Şifre (boş bırakılırsa değişmez)" : "Şifre *"}
                  </Label>
                  <Input
                    required={!isEdit}
                    minLength={isEdit ? undefined : 6}
                    type="text"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="rounded-sm font-mono"
                    placeholder={isEdit ? "Değiştirmek için yazın" : "En az 6 karakter"}
                    data-testid="staff-password"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="label-eyebrow">Cep Telefonu</Label>
                  <Input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="0532 000 00 00"
                    className="rounded-sm font-mono"
                    data-testid="staff-phone"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    PayTR canlı mod için zorunlu. Rakamlardan oluşan en az 10 hane.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label className="label-eyebrow">Rol</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })} disabled={isEdit && editingId === user?.id}>
                    <SelectTrigger className="rounded-sm" data-testid="staff-role" title={isEdit && editingId === user?.id ? "Kendi yetkisini değiştiremezsiniz" : ""}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="personel">Personel</SelectItem>
                      <SelectItem value="owner">Yönetici (Owner)</SelectItem>
                    </SelectContent>
                  </Select>
                  {isEdit && editingId === user?.id && (
                    <p className="text-[11px] text-amber-600 font-semibold">
                      ⚠️ Farklı bir yönetici bulunmadığı için yetki değiştirilemez.
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div>
              <div className="label-eyebrow text-amber-600 mb-2">Adres</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="label-eyebrow">Adres</Label>
                  <Textarea
                    rows={2}
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    placeholder="Sokak, mahalle, bina no, daire"
                    className="rounded-sm"
                    data-testid="staff-address"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="label-eyebrow">İlçe</Label>
                  <Input
                    value={form.district}
                    onChange={(e) => setForm({ ...form, district: e.target.value })}
                    className="rounded-sm"
                    data-testid="staff-district"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="label-eyebrow">İl</Label>
                  <Input
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    className="rounded-sm"
                    data-testid="staff-city"
                  />
                </div>
              </div>
            </div>

            <div>
              <div className="label-eyebrow text-amber-600 mb-2">Fatura Bilgileri</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="label-eyebrow">Fatura Tipi</Label>
                  <Select value={form.invoice_type} onValueChange={(v) => setForm({ ...form, invoice_type: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="staff-invoice-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="individual">Bireysel</SelectItem>
                      <SelectItem value="company">Kurumsal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="label-eyebrow">Fatura E-postası</Label>
                  <Input
                    type="email"
                    value={form.billing_email}
                    onChange={(e) => setForm({ ...form, billing_email: e.target.value })}
                    placeholder="muhasebe@firma.com"
                    className="rounded-sm"
                    data-testid="staff-billing-email"
                  />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="label-eyebrow">
                    {form.invoice_type === "company" ? "Ünvan" : "Ad Soyad (Fatura)"}
                  </Label>
                  <Input
                    value={form.invoice_title}
                    onChange={(e) => setForm({ ...form, invoice_title: e.target.value })}
                    placeholder={form.invoice_type === "company" ? "Firma ünvanı" : "Faturada görünecek isim"}
                    className="rounded-sm"
                    data-testid="staff-invoice-title"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="label-eyebrow">
                    {form.invoice_type === "company" ? "Vergi No" : "TC Kimlik No"}
                  </Label>
                  <Input
                    value={form.tax_no}
                    onChange={(e) => setForm({ ...form, tax_no: e.target.value })}
                    className="rounded-sm font-mono"
                    data-testid="staff-tax-no"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="label-eyebrow">
                    Vergi Dairesi {form.invoice_type === "company" ? "" : "(opsiyonel)"}
                  </Label>
                  <Input
                    value={form.tax_office}
                    onChange={(e) => setForm({ ...form, tax_office: e.target.value })}
                    className="rounded-sm"
                    data-testid="staff-tax-office"
                  />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="label-eyebrow">Fatura Adresi (Adresten farklıysa)</Label>
                  <Textarea
                    rows={2}
                    value={form.invoice_address}
                    onChange={(e) => setForm({ ...form, invoice_address: e.target.value })}
                    placeholder="Boş bırakılırsa yukarıdaki adres kullanılır"
                    className="rounded-sm"
                    data-testid="staff-invoice-address"
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-sm">İptal</Button>
              <Button type="submit" disabled={saving} className="rounded-sm bg-foreground text-background hover:bg-foreground/90" data-testid="staff-submit">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (isEdit ? "Kaydet" : "Ekle")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display tracking-tight">Personel silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              Bu personel artık sisteme giriş yapamayacak.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
